import { Command, Option } from "clipanion";
import { M8tCommand } from "../lib/m8t-command.js";
import { DefaultAzureCredential } from "@azure/identity";
import { getAzAccount, getBearerToken, getFoundryToken, getCallerObjectId } from "../lib/auth.js";
import { readFoundryConfig } from "../lib/foundry-config.js";
import { resolveGatewayContext } from "../lib/gateway-context.js";
import { runAz } from "../lib/az.js";
import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import {
  checkAzSignedIn,
  checkFoundryConfig,
  checkTenantAlignment,
  checkGatewayReachable,
  checkDataPlane,
  checkReasoningCapacity,
  checkDeliveryGrant,
  checkModelQuota,
  checkRepoRoot,
  type RepoRootProbe,
  type CheckResult,
  type ModelDeployment,
} from "../lib/doctor-checks.js";
import { listModelQuota } from "../lib/quota.js";
import { getAgentVersion } from "../lib/foundry-agent-get.js";
import { getAgentIdentityPrincipalId } from "../lib/foundry-agents.js";
import { resolveKeyVaultResourceId, principalHasKvSecretsUser } from "../lib/rbac.js";
import { colors, renderJson, resolveOutputMode } from "../lib/output.js";

async function resolveFoundryAccountId(endpoint: string): Promise<string | null> {
  const m = /^https:\/\/([^.]+)\.services\.ai\.azure\.com/.exec(endpoint);
  if (!m) return null;
  try {
    const out = await runAz([
      "cognitiveservices", "account", "list",
      "--query", `[?name=='${m[1]}'].id`,
      "--output", "json",
    ]);
    const ids = JSON.parse(out) as string[];
    return ids[0] ?? null;
  } catch {
    return null;
  }
}

function parseAccountId(id: string): { rg: string; name: string } | null {
  const m = /\/resourceGroups\/([^/]+)\/.*\/accounts\/([^/]+)$/i.exec(id);
  return m ? { rg: m[1], name: m[2] } : null;
}

async function listModelDeployments(accountId: string): Promise<ModelDeployment[]> {
  const parsed = parseAccountId(accountId);
  if (!parsed) return [];
  try {
    const out = await runAz([
      "cognitiveservices", "account", "deployment", "list",
      "-g", parsed.rg, "-n", parsed.name,
      "--output", "json",
    ]);
    const raw = JSON.parse(out) as {
      name?: string;
      sku?: { capacity?: number };
      properties?: { model?: { name?: string } };
    }[];
    return raw.map((d) => ({
      name: d.name ?? "",
      model: d.properties?.model?.name ?? "",
      capacity: d.sku?.capacity ?? 0,
    }));
  } catch {
    return [];
  }
}

async function resolveAccountLocation(accountId: string): Promise<string | null> {
  const parsed = parseAccountId(accountId);
  if (!parsed) return null;
  try {
    const out = await runAz(["cognitiveservices", "account", "show", "-g", parsed.rg, "-n", parsed.name, "--query", "location", "--output", "tsv"]);
    return out.trim() || null;
  } catch {
    return null;
  }
}

function probeRepoRoot(): RepoRootProbe {
  const marker = path.join(os.homedir(), ".m8t-stack", "repo-root");
  if (!fs.existsSync(marker)) {
    return { markerExists: false, path: null, isDir: false, isGitCheckout: false };
  }
  let repoPath: string;
  try {
    repoPath = fs.readFileSync(marker, "utf8").trim();
  } catch {
    return { markerExists: true, path: null, isDir: false, isGitCheckout: false };
  }
  if (!repoPath) return { markerExists: true, path: null, isDir: false, isGitCheckout: false };
  let isDir = false;
  try { isDir = fs.statSync(repoPath).isDirectory(); } catch { isDir = false; }
  const isGitCheckout = isDir && fs.existsSync(path.join(repoPath, ".git"));
  return { markerExists: true, path: repoPath, isDir, isGitCheckout };
}

export class DoctorCommand extends M8tCommand {
  static paths = [["doctor"]];
  static usage = Command.Usage({
    description: "Diagnose the local m8t setup: az login, config.yaml, gateway, Foundry data-plane.",
  });

  output = Option.String("--output");
  agent = Option.String("--agent");
  resourceGroup = Option.String("--resource-group", {
    description: "m8t-stack resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const json = mode === "json";

    const glyph: Record<string, string> = {
      PASS: colors.success("PASS"),
      WARN: colors.hint("WARN"),
      FAIL: colors.error("FAIL"),
      INFO: colors.dim("INFO"),
    };

    // Several checks do network I/O (gateway probe, Foundry data-plane probe)
    // that can take a few seconds. In pretty mode we STREAM each result the
    // moment it resolves so the terminal isn't dead; JSON mode accumulates and
    // emits one object at the end so `--output json` stays parseable.
    const checks: CheckResult[] = [];
    const emit = (c: CheckResult): void => {
      checks.push(c);
      if (json) return;
      this.context.stdout.write(`[${glyph[c.status]}] ${c.name}: ${c.detail}\n`);
      if (c.remediation) {
        this.context.stdout.write(colors.hint(`        fix: ${c.remediation}`) + "\n");
      }
    };
    // Activity hint before a slow probe → stderr (keeps stdout clean for pipes).
    const checking = (what: string): void => {
      if (!json) this.context.stderr.write(colors.dim(`  checking ${what}…`) + "\n");
    };

    if (!json) this.context.stdout.write(colors.dim("running m8t doctor…") + "\n");

    // Fast local checks.
    const az = await getAzAccount().catch(() => null);
    emit(checkAzSignedIn(az));
    const foundry = await readFoundryConfig();
    emit(checkFoundryConfig(foundry));
    emit(checkTenantAlignment(az, foundry));
    emit(checkRepoRoot(probeRepoRoot()));

    // Gateway probe (network): discover + GET /api/me with a bearer token.
    checking("gateway");
    let gw = { ok: false, detail: "not probed" };
    try {
      const ctx = await resolveGatewayContext({ interactive: false, resourceGroup: this.resourceGroup });
      const token = await getBearerToken(ctx.gatewayClientId);
      const res = await fetch(`${ctx.gatewayUrl}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      gw = { ok: res.ok, detail: `HTTP ${res.status.toString()} on ${ctx.gatewayUrl}/api/me` };
    } catch (e) {
      gw = { ok: false, detail: (e as Error).message };
    }
    emit(checkGatewayReachable(gw));

    // Data-plane probe (network, only if we have a project endpoint).
    if (foundry) {
      checking("Foundry data-plane");
      let status = 0;
      try {
        const token = await getFoundryToken();
        const res = await fetch(`${foundry.projectEndpoint}/assistants?api-version=2025-05-01`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        status = res.status;
      } catch {
        status = 0;
      }
      const foundryAccountId = await resolveFoundryAccountId(foundry.projectEndpoint);
      const oid = await getCallerObjectId().catch(() => null);
      emit(checkDataPlane({ status, foundryAccountId, oid }));

      // Capacity preflight: warn if the reasoning/brain model deployment is
      // under ~250 TPM (F3.5 hit 429s at the Foundry default 50). Reuses the
      // foundryAccountId resolved above; INFO-degrades on any az/parse error.
      checking("model capacity");
      const deployments = foundryAccountId ? await listModelDeployments(foundryAccountId) : [];
      emit(checkReasoningCapacity(deployments));

      checking("model quota");
      const region = foundryAccountId ? await resolveAccountLocation(foundryAccountId) : null;
      const usages = region ? await listModelQuota(region) : [];
      emit(checkModelQuota(deployments, usages));

      // Targeted delivery-grant net: when --agent is given, verify that agent can
      // read its brain token. Uses only proven single-agent REST calls; INFO-degrades.
      if (typeof this.agent === "string" && this.agent) {
        checking(`delivery grant for ${this.agent}`);
        try {
          const credential = new DefaultAzureCredential();
          const cur = await getAgentVersion({
            credential,
            projectEndpoint: foundry.projectEndpoint,
            agentName: this.agent,
          });
          const agentEnv = ((cur.definition as Record<string, unknown>).environment_variables as Partial<Record<string, string>> | undefined) ?? {};
          const installationId = agentEnv.GITHUB_APP_INSTALLATION_ID?.trim();
          const kvUri = agentEnv.AZURE_KEYVAULT_URI?.trim();
          if (installationId && kvUri) {
            const principalId = await getAgentIdentityPrincipalId({
              credential,
              endpoint: foundry.projectEndpoint,
              name: this.agent,
              version: cur.version,
            });
            const subscriptionId = az?.subscriptionId;
            if (!subscriptionId) {
              emit({ name: "delivery KV grant", status: "INFO", detail: `skipped '${this.agent}' — az not signed in (run az login)` });
            } else {
              const kvScope = await resolveKeyVaultResourceId({ credential, subscriptionId, kvUri });
              const hasGrant = await principalHasKvSecretsUser({ credential, kvScope, principalId });
              emit(checkDeliveryGrant([{ agentName: this.agent, hasDeliveryEnv: true, hasGrant }]));
            }
          } else {
            emit(checkDeliveryGrant([{ agentName: this.agent, hasDeliveryEnv: false, hasGrant: false }]));
          }
        } catch (e) {
          emit({ name: "delivery KV grant", status: "INFO", detail: `could not check '${this.agent}': ${(e as Error).message}` });
        }
      }
    } else {
      emit({ name: "foundry data-plane", status: "INFO", detail: "skipped — no config.yaml" });
    }

    const failed = checks.some((c) => c.status === "FAIL");
    if (json) {
      this.context.stdout.write(renderJson({ ok: !failed, checks }) + "\n");
    }
    return failed ? 1 : 0;
  }
}
